import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../axios";
import { toast } from "react-toastify";
import { Trophy, FileText, Unlock, RefreshCw, X, Loader2 } from "lucide-react";

function OfflineCompletedAssess() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [studentListByAssessmentId, setStudentListByAssessmentId] = useState(
    [],
  );

  // ─── Re-evaluation States ──────────
  const [isReevaluating, setIsReevaluating] = useState(false);
  const [localStudentList, setLocalStudentList] = useState([]);
  const [originalSnapshot, setOriginalSnapshot] = useState([]);
  const [gradeOptions, setGradeOptions] = useState([]);
  const [isSubmittingReeval, setIsSubmittingReeval] = useState(false);

  // ─── Fetch Student Results ──────────
  useEffect(() => {
    const getStudentList = async () => {
      try {
        let res = await axios.get(
          `/api/v1/assessment/studentAssessment/getByAssessmentId/${state?.assessmentId}`,
        );
        if (res?.data?.data) {
          setStudentListByAssessmentId(res.data.data);
        }
      } catch (error) {
        console.log(error);
      }
    };
    getStudentList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.assessmentId]);

  // ─── Sync local list when data loads ──────────
  useEffect(() => {
    if (studentListByAssessmentId.length > 0 && !isReevaluating) {
      setLocalStudentList([...studentListByAssessmentId]);
      setOriginalSnapshot(
        JSON.parse(JSON.stringify(studentListByAssessmentId)),
      );
    }
  }, [studentListByAssessmentId, isReevaluating]);

  // ─── Fetch Grade Scale Options ──────────
  useEffect(() => {
    const fetchGradeScale = async () => {
      const gScaleId = state?.assessment?.gradeScaleId;
      if (!gScaleId || entryType !== "Grade") return;
      try {
        const schoolId = JSON.parse(localStorage.getItem("schoolId"));
        const res = await axios.get(
          `/api/v1/performance/grade-scales?schoolId=${schoolId}`,
        );
        const allScales = res?.data?.data || res?.data || [];
        const matched = allScales.find(
          (s) => s._id === gScaleId || s.id === gScaleId,
        );
        if (matched?.grades) {
          setGradeOptions(matched.grades);
        }
      } catch (error) {
        console.log("Error fetching grade scale:", error);
      }
    };
    fetchGradeScale();
  }, [state?.assessment?.gradeScaleId]);

  // ─── Detect Entry Type ──────────
  const entryType =
    state?.assessment?.entryType === "Grade" ? "Grade" : "Marks";

  // ─── Event & Type ──────────
  const eventName = state?.assessment?.assessmentEventId?.name || "N/A";
  const typeName = state?.assessment?.assessmentTypeId?.name || "N/A";
  const subjectColor =
    "#" + (state?.assessment?.subject_id?.subject_color || "000000");

  // ─── Assessment data for header ──────────
  const assessmentData = studentListByAssessmentId[0]?.assessment_id;

  // ─── Check if student value changed ──────────
  const hasStudentChanged = (index) => {
    if (!originalSnapshot[index] || !localStudentList[index]) return false;
    const orig = originalSnapshot[index];
    const curr = localStudentList[index];
    if (entryType === "Marks") {
      return (
        Number(orig.obtained_marks ?? 0) !== Number(curr.obtained_marks ?? 0)
      );
    }
    return (orig.obtained_grade || "") !== (curr.obtained_grade || "");
  };

  // ─── Activate Re-evaluation ──────────
  const handleActivateReevaluation = () => {
    setLocalStudentList([...studentListByAssessmentId]);
    setOriginalSnapshot(JSON.parse(JSON.stringify(studentListByAssessmentId)));
    setIsReevaluating(true);
    toast.info(
      "Re-evaluation mode activated. Edit marks/grades and provide reasons for changes.",
    );
  };

  // ─── Cancel Re-evaluation ──────────
  const handleCancelReevaluation = () => {
    setIsReevaluating(false);
    setLocalStudentList(JSON.parse(JSON.stringify(originalSnapshot)));
  };

  // ─── Local input change ──────────
  const handleLocalChange = (index, field, value) => {
    const updated = [...localStudentList];
    updated[index] = { ...updated[index], [field]: value };
    setLocalStudentList(updated);
  };

  // ─── Validate & Submit Re-evaluation ──────────
  const handleSubmitReevaluation = async () => {
    const changedIndices = localStudentList
      .map((_, i) => i)
      .filter((i) => hasStudentChanged(i));

    if (changedIndices.length === 0) {
      toast.warning("No changes detected.");
      return;
    }

    // Check missing reasons
    const missingReasons = changedIndices.filter(
      (i) => !localStudentList[i].remark?.trim(),
    );
    if (missingReasons.length > 0) {
      const names = missingReasons
        .map((i) => localStudentList[i].student_id?.name)
        .join(", ");
      toast.error(`Reason is required for changed students: ${names}`);
      return;
    }

    setIsSubmittingReeval(true);
    try {
      const changedStudents = changedIndices.map((i) => localStudentList[i]);

      const bulkPayload = {
        students: changedStudents.map((s) => ({
          id: null,
          student_id: s.student_id?.id,
          remark: s.remark || "",
          ...(entryType === "Marks"
            ? { obtained_marks: Number(s.obtained_marks ?? 0) }
            : { obtained_grade: s.obtained_grade || "" }),
        })),
        teacher_id: null,
      };

      await axios.put(
        `/api/v1/assessment/${state.assessmentId}/studentResult/updateBulk`,
        bulkPayload,
      );

      toast.success("Re-evaluation submitted successfully!");
      setIsReevaluating(false);
      setStudentListByAssessmentId([...localStudentList]);
      setOriginalSnapshot(JSON.parse(JSON.stringify(localStudentList)));
    } catch (error) {
      console.log(error);
      toast.error(
        error?.response?.data?.message || "Failed to submit re-evaluation",
      );
    } finally {
      setIsSubmittingReeval(false);
    }
  };

  // ─── Get marks from grade ──────────
  const getMarksFromGrade = (gradeValue) => {
    const g = gradeOptions.find((opt) => opt.grade === gradeValue);
    return g ? g.maxMarks : 0;
  };

  return (
    <div
      className="create_wrap w-100 px-3 orange-scrollBar"
      style={{ overflowY: "scroll", height: "calc(100vh - 100px)" }}
    >
      {/* ─── Back Navigation ────────── */}
      <div
        className="mb-2"
        onClick={() => navigate("/offline-performance/academic")}
      >
        <div
          className="d-flex align-items-center gap-2"
          style={{ cursor: "pointer" }}
        >
          <div>
            <img src="/images/Back Arrow.svg" alt="" />
          </div>
          <div className="d-flex flex-column gap-1">
            <span className="head_manual">Student Marks Entry (Offline)</span>
            <span className="fw-small text-muted" style={{ fontSize: "10px" }}>
              Dashboard / Performance / Completed / Student Marks Entry
              (Offline)
            </span>
          </div>
        </div>
      </div>

      {/* ─── Main Card ────────── */}
      <div
        className="w-100 py-2 px-4 rounded-3"
        style={{ backgroundColor: "#fff", height: "100%" }}
      >
        {/* ─── Header ────────── */}
        <div className="class-section-subject d-flex align-items-start justify-content-between gap-2 ml-3">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="fs-4 poppins-semibold">
              <span>
                {assessmentData?.standard?.name}
                {" - "}
                {assessmentData?.section?.name}
              </span>
              {" | "}
              <span>{assessmentData?.subject?.name}</span>
            </div>

            {/* Event Badge */}
            <span
              className="badge"
              style={{
                backgroundColor: "#8D4B28",
                color: "#fff",
                fontSize: "11px",
                fontWeight: 600,
                padding: "4px 10px",
                borderRadius: "4px",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Trophy size={12} />
              {eventName}
            </span>

            {/* Type Badge */}
            <span
              className="badge"
              style={{
                backgroundColor: subjectColor,
                color: "#fff",
                fontSize: "11px",
                fontWeight: 600,
                padding: "4px 10px",
                borderRadius: "4px",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <FileText size={12} />
              {typeName}
            </span>

            {/* Entry Type Badge */}
            <span
              className="badge px-3 py-2"
              style={{
                backgroundColor: entryType === "Marks" ? "#28a745" : "#ffc107",
                color: entryType === "Marks" ? "white" : "black",
                fontSize: "13px",
                fontWeight: "500",
                borderRadius: "20px",
              }}
            >
              {entryType === "Marks" ? "📝 Marks Entry" : "📊 Grades Entry"}
            </span>

            {/* Re-evaluating Badge */}
            {isReevaluating && (
              <span
                className="badge px-3 py-2"
                style={{
                  backgroundColor: "#3b82f6",
                  color: "white",
                  fontSize: "13px",
                  fontWeight: "500",
                  borderRadius: "20px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Unlock size={14} />
                Re-evaluating
              </span>
            )}
          </div>

          {/* ─── Action Buttons ────────── */}
          <div className="ms-auto d-flex gap-2">
            {!isReevaluating ? (
              <button
                className="btn d-flex align-items-center gap-2"
                onClick={handleActivateReevaluation}
                style={{
                  backgroundColor: "#eff6ff",
                  color: "#2563eb",
                  border: "1px solid #bfdbfe",
                  fontSize: "13px",
                  fontWeight: 600,
                  padding: "6px 14px",
                  borderRadius: "8px",
                }}
              >
                <Unlock size={16} />
                Activate Re-evaluation
              </button>
            ) : (
              <>
                <button
                  className="btn d-flex align-items-center gap-2"
                  onClick={handleCancelReevaluation}
                  style={{
                    backgroundColor: "#f3f4f6",
                    color: "#6b7280",
                    border: "1px solid #e5e7eb",
                    fontSize: "13px",
                    fontWeight: 600,
                    padding: "6px 14px",
                    borderRadius: "8px",
                  }}
                >
                  <X size={16} />
                  Cancel
                </button>
                <button
                  className="btn d-flex align-items-center gap-2"
                  onClick={handleSubmitReevaluation}
                  disabled={isSubmittingReeval}
                  style={{
                    backgroundColor: isSubmittingReeval ? "#93c5fd" : "#2563eb",
                    color: "white",
                    border: "none",
                    fontSize: "13px",
                    fontWeight: 600,
                    padding: "6px 14px",
                    borderRadius: "8px",
                    cursor: isSubmittingReeval ? "not-allowed" : "pointer",
                  }}
                >
                  {isSubmittingReeval ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <RefreshCw size={16} />
                  )}
                  {isSubmittingReeval
                    ? "Submitting..."
                    : "Submit Re-evaluation"}
                </button>
              </>
            )}
          </div>
        </div>

        {/* ─── Table ────────── */}
        {state.assessmentId && (
          <div className="mid-student-table">
            <div className="table-container-offlineSub">
              <div className="scrollable-table-wrapper">
                <table className="student-table-offlineSub">
                  <thead style={{ position: "sticky", top: "0" }}>
                    <tr>
                      <th style={{ width: "22%" }}>Name</th>
                      <th style={{ width: "12%" }}>Roll No.</th>
                      <th style={{ width: "66%" }}>
                        {entryType === "Marks"
                          ? "Obtained Marks"
                          : "Obtained Grade"}
                        <div className="sub-header-offlineSub">
                          <div className="heading-text">
                            Total {entryType === "Marks" ? "Marks" : "Grades"}:{" "}
                            {entryType === "Marks"
                              ? assessmentData?.total_marks || 0
                              : "Select Grade"}
                          </div>
                          <div className="heading-text">
                            Duration: {assessmentData?.total_duration || "N/A"}{" "}
                            min
                          </div>
                        </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {localStudentList.map((student, index) => {
                      const changed = hasStudentChanged(index);
                      const showReason = isReevaluating && changed;
                      const isReasonRequired = isReevaluating && changed;

                      return (
                        <tr key={index}>
                          <td style={{ width: "22%" }}>
                            {student?.student_id?.name}
                          </td>
                          <td style={{ width: "12%" }}>
                            {student?.student_id?.roll_number}
                          </td>
                          <td style={{ width: "66%" }}>
                            {/* Marks / Grade Input */}
                            <div style={{ width: "100%" }}>
                              {entryType === "Grade" ? (
                                <>
                                  <select
                                    className="form-select grade-dropdown"
                                    value={student?.obtained_grade || ""}
                                    disabled={!isReevaluating}
                                    onChange={(e) =>
                                      handleLocalChange(
                                        index,
                                        "obtained_grade",
                                        e.target.value,
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      padding: "6px 8px",
                                      borderRadius: "6px",
                                      border: changed
                                        ? "1px solid #2563eb"
                                        : "1px solid #ddd",
                                      fontSize: "14px",
                                      backgroundColor: isReevaluating
                                        ? "white"
                                        : "#f5f5f5",
                                      cursor: isReevaluating
                                        ? "pointer"
                                        : "not-allowed",
                                      minHeight: "38px",
                                    }}
                                  >
                                    <option value="">
                                      {isReevaluating
                                        ? "Select Grade"
                                        : student?.obtained_grade || "No Grade"}
                                    </option>
                                    {isReevaluating &&
                                      gradeOptions.map((g, idx) => (
                                        <option key={idx} value={g.grade}>
                                          {g.grade} ({g.minMarks}-{g.maxMarks})
                                        </option>
                                      ))}
                                  </select>
                                  {student?.obtained_grade && (
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6c757d",
                                        marginTop: "2px",
                                      }}
                                    >
                                      Grade: {student.obtained_grade}
                                      {isReevaluating &&
                                        ` | Marks: ${getMarksFromGrade(
                                          student.obtained_grade,
                                        )}`}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={
                                    isReevaluating
                                      ? (student?.obtained_marks ?? "")
                                      : (student?.obtained_marks ?? 0)
                                  }
                                  readOnly={!isReevaluating}
                                  onFocus={(e) => {
                                    if (
                                      isReevaluating &&
                                      (e.target.value === "0" ||
                                        e.target.value === 0)
                                    ) {
                                      e.target.value = "";
                                    }
                                  }}
                                  onChange={(e) => {
                                    let value = e.target.value;
                                    if (!/^\d*\.?\d*$/.test(value)) return;
                                    const num = parseFloat(value);
                                    const maxMarks =
                                      assessmentData?.total_marks || 100;
                                    if (!isNaN(num) && num > maxMarks) {
                                      value = String(maxMarks);
                                    }
                                    handleLocalChange(
                                      index,
                                      "obtained_marks",
                                      value,
                                    );
                                  }}
                                  style={{
                                    width: "100%",
                                    padding: "6px 8px",
                                    borderRadius: "6px",
                                    border: changed
                                      ? "1px solid #2563eb"
                                      : "1px solid #ddd",
                                    fontSize: "14px",
                                    backgroundColor: isReevaluating
                                      ? "white"
                                      : "#f5f5f5",
                                    transition: "all 0.2s",
                                  }}
                                />
                              )}
                            </div>

                            {/* ─── Reason Field (only when changed) ────────── */}
                            {showReason && (
                              <div style={{ marginTop: "6px" }}>
                                <div
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    color: "#374151",
                                    marginBottom: "3px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  Reason for change
                                  {isReasonRequired && (
                                    <span style={{ color: "#dc3545" }}>*</span>
                                  )}
                                  {isReasonRequired &&
                                    !student?.remark?.trim() && (
                                      <span
                                        style={{
                                          fontSize: "10px",
                                          color: "#dc3545",
                                          fontWeight: 500,
                                        }}
                                      >
                                        (required)
                                      </span>
                                    )}
                                </div>
                                <input
                                  type="text"
                                  value={student?.remark ?? ""}
                                  onChange={(e) =>
                                    handleLocalChange(
                                      index,
                                      "remark",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Enter reason for change..."
                                  style={{
                                    width: "100%",
                                    padding: "6px 10px",
                                    borderRadius: "6px",
                                    border:
                                      isReasonRequired &&
                                      !student?.remark?.trim()
                                        ? "1px solid #dc3545"
                                        : "1px solid #d1d5db",
                                    fontSize: "13px",
                                    backgroundColor: "#fff",
                                    outline: "none",
                                  }}
                                />
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OfflineCompletedAssess;
